const APPLE_SCRIPT = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const CLIENT_ID = 'ru.quoridorapp.signin';
const REDIRECT_URI = import.meta.env.VITE_APPLE_REDIRECT_URI
  ?? (typeof window !== 'undefined' ? `${window.location.origin}/auth/apple/callback` : 'https://quoridorapp.ru/auth/apple/callback');

let initPromise: Promise<void> | null = null;

function prefersRedirectFlow(): boolean {
  const ua = navigator.userAgent;
  const mobile = /iPhone|iPad|iPod|Android/i.test(ua);
  const ios = /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const safari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  // Popup часто зависает в iOS Safari — используем полный redirect.
  return mobile || (ios && safari);
}

function loadAppleScript(): Promise<void> {
  if (window.AppleID) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_SCRIPT}"]`);
    if (existing) {
      if (window.AppleID) { resolve(); return; }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Не удалось загрузить Apple Sign In')));
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_SCRIPT;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Не удалось загрузить Apple Sign In'));
    document.head.appendChild(script);
  });
}

export async function initAppleSignIn(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    await loadAppleScript();
    if (!window.AppleID?.auth) throw new Error('Apple Sign In недоступен');
    window.AppleID.auth.init({
      clientId: CLIENT_ID,
      scope: 'name email',
      redirectURI: REDIRECT_URI,
      state: `web-${Date.now()}`,
      usePopup: !prefersRedirectFlow(),
    });
  })();
  return initPromise;
}

export interface AppleSignInResult {
  identityToken: string;
  nickname?: string;
}

function waitForPopupResult(timeoutMs = 120_000): Promise<AppleSignInResult> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Apple ID не ответил. Попробуйте ещё раз.'));
    }, timeoutMs);

    const onSuccess = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        authorization?: { id_token?: string };
        user?: { name?: { firstName?: string; lastName?: string } };
      };
      const token = detail?.authorization?.id_token;
      if (!token) {
        cleanup();
        reject(new Error('Apple не вернул токен авторизации'));
        return;
      }
      let nickname: string | undefined;
      const user = detail.user;
      if (user?.name) {
        const parts = [user.name.firstName, user.name.lastName].filter(Boolean);
        if (parts.length) nickname = parts.join(' ');
      }
      cleanup();
      resolve({ identityToken: token, nickname });
    };

    const onFailure = (event: Event) => {
      const detail = (event as CustomEvent).detail as { error?: string };
      cleanup();
      if (detail?.error === 'popup_closed_by_user') {
        reject(new Error('popup_closed_by_user'));
      } else {
        reject(new Error(detail?.error || 'Не удалось войти через Apple'));
      }
    };

    function cleanup() {
      window.clearTimeout(timer);
      document.removeEventListener('AppleIDSignInOnSuccess', onSuccess);
      document.removeEventListener('AppleIDSignInOnFailure', onFailure);
    }

    document.addEventListener('AppleIDSignInOnSuccess', onSuccess);
    document.addEventListener('AppleIDSignInOnFailure', onFailure);
  });
}

export async function signInWithApple(): Promise<AppleSignInResult> {
  await initAppleSignIn();

  if (prefersRedirectFlow()) {
    void window.AppleID!.auth.signIn();
    return new Promise(() => { /* страница уходит на appleid.apple.com */ });
  }

  const popupPromise = waitForPopupResult();
  await window.AppleID!.auth.signIn();
  return popupPromise;
}

export function parseAppleCallback(search: string): AppleSignInResult | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const idToken = params.get('id_token');
  if (!idToken) return null;
  let nickname: string | undefined;
  const userRaw = params.get('user');
  if (userRaw) {
    try {
      const user = JSON.parse(userRaw) as { name?: { firstName?: string; lastName?: string } };
      const parts = [user.name?.firstName, user.name?.lastName].filter(Boolean);
      if (parts.length) nickname = parts.join(' ');
    } catch { /* ignore */ }
  }
  return { identityToken: idToken, nickname };
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state?: string;
          usePopup?: boolean;
        }) => void;
        signIn: () => Promise<unknown>;
      };
    };
  }
}
